import { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Database,
    FileText,
    FunctionSquare,
    ShieldCheck,
    Code,
    MoreVertical,
    Pencil,
    Trash2,
    Copy,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
    PolicyElement,
    Condition,
    RegoValue,
    ConstantElement,
    SingleValueRuleElement,
    SetRuleElement,
    ObjectRuleElement,
    FunctionElement,
    RawRegoElement,
} from '@/types/policy-editor';

export type CardCategory = 'decision' | 'data' | 'rule' | 'function' | 'raw';

const CATEGORY_STYLES: Record<CardCategory, { border: string; bg: string; iconBg: string; iconText: string; hoverBg: string }> = {
    decision: { border: 'border-emerald-200', bg: 'bg-emerald-50/40', iconBg: 'bg-emerald-100', iconText: 'text-emerald-700', hoverBg: 'hover:bg-emerald-50/60' },
    data:     { border: 'border-amber-200',   bg: 'bg-amber-50/40',   iconBg: 'bg-amber-100',   iconText: 'text-amber-700',   hoverBg: 'hover:bg-amber-50/60' },
    rule:     { border: 'border-blue-200',     bg: 'bg-blue-50/40',    iconBg: 'bg-blue-100',     iconText: 'text-blue-700',    hoverBg: 'hover:bg-blue-50/60' },
    function: { border: 'border-purple-200',   bg: 'bg-purple-50/40',  iconBg: 'bg-purple-100',   iconText: 'text-purple-700',  hoverBg: 'hover:bg-purple-50/60' },
    raw:      { border: 'border-slate-200',    bg: 'bg-slate-50/40',   iconBg: 'bg-slate-100',    iconText: 'text-slate-500',   hoverBg: 'hover:bg-slate-50/60' },
};

interface PolicyElementCardProps {
    element: PolicyElement;
    /** @deprecated Use `category` instead */
    isDecision?: boolean;
    category?: CardCategory;
    selected?: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    readOnly?: boolean;
}

export function PolicyElementCard({
    element,
    isDecision = false,
    category,
    selected = false,
    onEdit,
    onDelete,
    onDuplicate,
    readOnly = false,
}: PolicyElementCardProps) {
    const handleClick = useCallback(() => {
        if (!readOnly) onEdit();
    }, [readOnly, onEdit]);

    const resolvedCategory: CardCategory = category ?? (isDecision ? 'decision' : 'raw');
    const styles = CATEGORY_STYLES[resolvedCategory];
    const icon = getElementIcon(element, resolvedCategory);
    const summary = getElementSummary(element);
    const badge = getElementBadge(element);

    return (
        <div
            className={cn(
                'group relative flex flex-col rounded-lg border px-3 py-2.5 transition-colors',
                !readOnly && `cursor-pointer ${styles.hoverBg} hover:shadow-sm`,
                selected
                    ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-400/30'
                    : `${styles.border} ${styles.bg}`,
            )}
            onClick={handleClick}
            role={readOnly ? undefined : 'button'}
            tabIndex={readOnly ? undefined : 0}
            onKeyDown={(e) => {
                if (!readOnly && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onEdit();
                }
            }}
        >
            <div className="flex items-center gap-2">
                <div className={cn('shrink-0 rounded p-1', styles.iconBg, styles.iconText)}>
                    {icon}
                </div>
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-900" title={element.name}>
                    {element.name}
                </span>
                {badge && (
                    <Badge variant="outline" className="shrink-0 text-[9px] px-1 py-0 leading-4">
                        {badge}
                    </Badge>
                )}
                {!readOnly && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MoreVertical size={12} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                                <Pencil size={14} className="mr-2" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                                <Copy size={14} className="mr-2" />
                                Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="text-red-600"
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            >
                                <Trash2 size={14} className="mr-2" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
            <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-slate-500">
                {element.description || summary}
            </p>
        </div>
    );
}

function getElementIcon(el: PolicyElement, category: CardCategory) {
    if (category === 'decision') return <ShieldCheck size={16} />;
    switch (el.elementType) {
        case 'constant':
            return <Database size={16} />;
        case 'singleValueRule':
        case 'setRule':
        case 'objectRule':
            return <FileText size={16} />;
        case 'function':
            return <FunctionSquare size={16} />;
        case 'rawRego':
            return <Code size={16} />;
    }
}

function getElementBadge(el: PolicyElement): string | null {
    switch (el.elementType) {
        case 'constant':
            return el.isDefault ? 'default' : null;
        case 'singleValueRule':
            if (el.isDefault) return 'default';
            return el.conditions.length > 0 ? `${el.conditions.length} cond` : null;
        case 'setRule':
            return 'set';
        case 'objectRule':
            return 'object';
        case 'function':
            return `(${el.parameters.length} params)`;
        case 'rawRego':
            return 'raw';
    }
}

function getElementSummary(el: PolicyElement): string {
    switch (el.elementType) {
        case 'constant':
            return summarizeValue(el.value);
        case 'singleValueRule':
            return summarizeRule(el);
        case 'setRule':
            return summarizeSetRule(el);
        case 'objectRule':
            return summarizeObjectRule(el);
        case 'function':
            return summarizeFunction(el);
        case 'rawRego':
            return `Raw Rego block (${el.source.split('\n').length} lines)`;
    }
}

function summarizeValue(val: RegoValue): string {
    switch (val.kind) {
        case 'scalar':
            return val.value === null ? 'null' : String(val.value);
        case 'object':
            return `Object {${val.entries.length} keys}`;
        case 'array':
            return `Array [${val.items.length} items]`;
        case 'set':
            return `Set {${val.items.length} items}`;
        case 'reference':
            return val.path;
        case 'functionCall':
            return `${val.name}(...)`;
        case 'comprehension':
            return `${val.type} comprehension`;
        case 'raw':
            return val.text.slice(0, 60);
    }
}

function summarizeConditions(conditions: Condition[]): string {
    if (conditions.length === 0) return 'always applies';
    const parts = conditions.slice(0, 3).map(summarizeCondition);
    const suffix = conditions.length > 3 ? ` + ${conditions.length - 3} more` : '';
    return `When: ${parts.join(' AND ')}${suffix}`;
}

function summarizeCondition(c: Condition): string {
    switch (c.type) {
        case 'comparison':
            return `${c.left} ${c.operator} ${c.right}`;
        case 'membership':
            return `${c.term} ${c.negated ? 'not in' : 'in'} ${c.collection}`;
        case 'assignment':
            return `${c.variable} := ${c.expression}`;
        case 'iteration':
            return `some ${c.variables.join(', ')} in ${c.collection}`;
        case 'negation':
            return `not ${c.expression}`;
        case 'functionCall':
            return c.assignTo
                ? `${c.assignTo} := ${c.name}(${c.args.join(', ')})`
                : `${c.name}(${c.args.join(', ')})`;
        case 'raw':
            return c.text.slice(0, 40);
        default:
            return '...';
    }
}

function summarizeRule(el: SingleValueRuleElement): string {
    const valStr = summarizeValue(el.value);
    const condStr = summarizeConditions(el.conditions);
    return `= ${valStr} | ${condStr}`;
}

function summarizeSetRule(el: SetRuleElement): string {
    return `contains ${el.memberExpression} | ${summarizeConditions(el.conditions)}`;
}

function summarizeObjectRule(el: ObjectRuleElement): string {
    return `[${el.keyExpression}] = ${el.valueExpression} | ${summarizeConditions(el.conditions)}`;
}

function summarizeFunction(el: FunctionElement): string {
    const params = el.parameters.join(', ');
    return `(${params}) → ${el.returnValue} | ${summarizeConditions(el.conditions)}`;
}
